import React from 'react';

import {useNavigate} from 'react-router-dom';

import {ActionIcon, Avatar, Button, Group, Menu, Text, useMantineColorScheme} from '@mantine/core';
import {IconArrowLeft, IconArrowRight, IconColorSwatch, IconLogout, IconMessage, IconMessageReply, IconMessageStar, IconMoon, IconQuestionMark, IconRefresh, IconSun, IconUser, IconBug, IconUpload, IconCloudUpload, IconFileUpload, IconPlayerRecord, IconSettings} from '@tabler/icons-react';
import {useColorScheme} from '@mantine/hooks';
import {getIsTauriWebview, useIsMobile} from '@/utils/user_agent';
import {useModule} from '@/store/kysely_module';
import {useModals} from '@/modules/music_sniper/components/modals/modals_provider';
import {SearchBar} from '@/components/search_bar';
import {setPathnameForReason} from '@/modules/navigation/navigation_module';
import {OpenUploadFileModalButton} from '../../modals/upload_file_modal';
import {useCurrentObjects} from '@/store/modules/workspaces_module';
import {Breadcrumbs as MantineBreadcrumbs, Anchor} from '@mantine/core';
import {navigateToPathForReason} from '@/modules/navigation/navigation_module';

type Props = {
    sidebarOpened: boolean;
    toggleSidebar?: () => void;
}

const isTauriWebview = getIsTauriWebview();

export const Header = (props: Props) => {
    const loginFlow = useModule('LoginFlow');
    const localDesktopFilesModule = useModule('LocalDesktopFiles');
    const modals = useModals();

    const currentObject = useCurrentObjects();

    const colorScheme = useMantineColorScheme();
    const navigate = useNavigate();

    const showHamburgerMenu = useIsMobile();

    const themeButtonStyle: React.CSSProperties = {
        cursor: 'pointer',
        paddingTop: '3px',
        marginRight: '3px',
    };

    const sunIcon = (
        <IconSun
            style={themeButtonStyle}
        />
    );

    const moonIcon = (
        <IconMoon
            style={themeButtonStyle}
        />
    );

    // const themeIcon = !darkModeEnabled ? sunIcon : moonIcon;

    const operatingSystemColorScheme = useColorScheme();
    const mantineColorScheme = useMantineColorScheme();
    const mantineModule = useModule('Mantine');

    const themeIconSetting = mantineColorScheme.colorScheme === 'auto' ? operatingSystemColorScheme : mantineColorScheme.colorScheme;
    const themeIcon = themeIconSetting === 'light' ? sunIcon : moonIcon;

    const darkModeEnabled = themeIconSetting === 'dark';

    const loginProvider = loginFlow?.getLoginProvider();
    let authItems: React.ReactNode;

    const uiEntrypointsModule = useModule('UIEntrypoints');
    const settingsComponents = uiEntrypointsModule.settings.components;

    const userModule = useModule('User');

    const iconWrapperStyle: React.CSSProperties = {
        marginRight: '-4px',
        display: 'flex',
        alignItems: 'center',
    };

    if (loginProvider) {
        const {useLogout} = loginProvider;
        const logout = useLogout();
        authItems = (
            <Menu.Item
                className='user-logout'
                leftSection={<div style={iconWrapperStyle}><IconLogout size={16} /></div>}
                onClick={async () => {
                    modals.openModal('ConfirmationModal', {
                        title: 'Log out of your account?',
                        message: '',
                        confirmLabel: 'Log Out',
                        onConfirm: () => {
                            if (!userModule.isOfflineMode.getState()) {
                                logout();
                            } else {
                                userModule.isOfflineMode.setState(false);
                                userModule.currentUser.setState(null);
                                setPathnameForReason('redirect_to_main_due_to_logging_out_in_offline_mode', '/main');
                            }
                        },
                    });
                }}
            >
                Log out
            </Menu.Item>
        );
    }

    const actionMenu = (
        <Menu
            closeOnItemClick={true}
            zIndex={10}
        >
            <Menu.Target>
                <ActionIcon
                    variant="subtle"
                    size="lg"
                    aria-label="Settings"
                    styles={{
                        root: {
                            height: '100%',
                            minWidth: '60px',
                            borderRadius: 0,
                            '&:active': {
                                backgroundColor: 'var(--mantine-color-default-hover)',
                            },
                        },
                    }}
                >
                    <IconSettings
                        size={24}
                        style={{}}
                    />
                </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
                <Menu.Item
                    leftSection={<div style={iconWrapperStyle}><IconUser size={16} /></div>}
                    onClick={() => modals.openModal('EditProfileModal', {})}
                >
                    Edit profile
                </Menu.Item>
                {settingsComponents.map((component, i) => (
                    <Menu.Item
                        leftSection={<div style={iconWrapperStyle}><component.icon size={16} /></div>}
                        key={i}
                        onClick={() => component.onClick(modals)}
                    >
                        {component.label}
                    </Menu.Item>
                ))}
                <Menu.Item
                    leftSection={<div style={iconWrapperStyle}><IconColorSwatch size={16} /></div>}
                    onClick={() => modals.openModal('CustomModal', {
                        title: 'Change Theme',
                        form: mantineModule.ChangeThemeModalForm,
                    })}
                >
                    Change Theme
                </Menu.Item>
                {localDesktopFilesModule && process.env.NODE_ENV === 'development' && (
                    <Menu.Item
                        leftSection={<div style={iconWrapperStyle}><IconBug size={16} /></div>}
                        onClick={() => modals.openModal('CustomModal', {
                            title: 'Debug Bookmarks',
                            form: localDesktopFilesModule.BookmarkDebugModalForm,
                        })}
                    >
                        Debug Bookmarks
                    </Menu.Item>
                )}
                {authItems}
            </Menu.Dropdown>
        </Menu>
    );

    const isMobile = useIsMobile();

    return (
        <Group h="100%" pl="md" pr={isMobile ? 'md' : 0} style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
            <Group style={{display: 'flex', alignItems: 'center'}}>
                {/* <Burger
                    opened={props.sidebarOpened}
                    onClick={props.toggleSidebar}
                    // hidden={!showHamburgerMenu}
                    size="sm"
                /> */}
                {isMobile && currentObject.breadcrumbPairs.length > 0 ? (
                    <MantineBreadcrumbs>
                        {currentObject.breadcrumbPairs.map((pair, i) => {
                            const isLast = i === currentObject.breadcrumbPairs.length - 1;
                            return isLast ? (
                                <Text key={pair.id} fw='bold'>
                                    {pair.name}
                                </Text>
                            ) : (
                                <Anchor
                                    key={pair.id}
                                    onClick={e => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        navigateToPathForReason(navigate, 'navigate_to_dynamic_path_from_breadcrumbs', pair.url);
                                    }}
                                >
                                    {pair.name}
                                </Anchor>
                            );
                        })}
                    </MantineBreadcrumbs>
                ) : (
                    <img
                        style={{
                            cursor: 'pointer',
                            width: 'calc(3.125rem * var(--mantine-scale))',
                        }}
                        // onClick={props.toggleSidebar}
                        onClick={(() => {
                            navigateToPathForReason(navigate, 'navigate_to_dashboard_from_header_logo', '/dashboard');
                        })}
                        src='https://songdrive.app/assets/images/logo.png'
                    />
                )}
                {/* <Burger opened={props.sidebarOpened} onClick={props.toggleSidebar} size="sm" /> */}
                {isTauriWebview && (
                    <>
                        <IconArrowLeft className='pointer' onClick={() => navigate(-1)} />
                        <IconArrowRight className='pointer' onClick={() => navigate(1)} />
                        <IconRefresh className='pointer' onClick={() => window.location.reload()} />
                    </>
                )}
            </Group>

            {!isMobile && <SearchBar />}

            <Group style={{display: 'flex', alignItems: 'center', height: '100%'}} gap="md">
                {!isMobile && (
                    <OpenUploadFileModalButton component={({onClick}) => (
                        <Button
                            variant='default'
                            size="sm"
                            onClick={onClick}
                        >
                            <IconFileUpload
                                color='var(--mantine-color-text)'
                                size={20}
                                style={{verticalAlign: 'middle', marginTop: '-2px'}}
                            />
                            <Text ml="2" size="sm" c="dimmed">
                                Upload
                            </Text>
                        </Button>
                    )} />
                )}

                {isMobile && (
                    <div style={{display: 'none'}}>
                        <SearchBar />
                    </div>
                )}

                {/* <a
                    href={process.env.NPS_SURVEY_URL || 'https://survey.songdrive.app/s/g1gc57xake4cay08za53vpwt'}
                    target='_blank'
                    rel='noopener noreferrer'
                    style={{textDecoration: 'none', color: 'inherit'}}
                    id='send-feedback'
                >
                    <IconMessageStar
                        title='Send feedback'
                    />
                </a> */}
                {!isMobile && (
                    <div style={{height: '100%', display: 'flex', alignItems: 'stretch'}}>
                        {actionMenu}
                    </div>
                )}
            </Group>
        </Group>
    )
};
